import { Form, Submit, type SubmitHandler } from '@ff-it/form';
import { Box, Button, Card, Stack } from '@ff-it/ui';
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';

type ViewContainerProps = {
  header: ReactNode;
  edit?: () => void;
  children: ReactNode;
  testId?: string;
};

function ViewContainer({ header, edit, children, testId }: ViewContainerProps): ReactElement {
  return (
    <Card
      header={header}
      testId={testId}
      footer={
        edit && (
          <Box>
            <Button testId="toggle-edit-card" variant="secondary" onClick={edit}>
              Edit
            </Button>
          </Box>
        )
      }
    >
      {children}
    </Card>
  );
}

type EditContainerProps = {
  header: ReactNode;
  cancel: () => void;
  children: ReactNode;
  initialValues: any;
  validate?: any;
  onSubmit: SubmitHandler<any>;
  testId?: string;
};

function EditContainer({
  header,
  cancel,
  children,
  initialValues,
  onSubmit,
  validate,
  testId,
}: EditContainerProps): ReactElement {
  return (
    <Form noValidate autoComplete="off" onSubmit={onSubmit} initialValues={initialValues} validate={validate}>
      <Card
        header={header}
        testId={testId}
        footer={
          <Stack direction="horizontal">
            <Submit data-test-id="update">Update</Submit>
            <Button variant="secondary" onClick={cancel}>
              Cancel
            </Button>
          </Stack>
        }
      >
        {children}
      </Card>
    </Form>
  );
}

export type PricelistEditCardProps<S extends string> = {
  header?: ReactNode;
  view: ReactNode;
  viewHeader?: ReactNode;
  edit: ReactNode;
  editHeader?: ReactNode;
  onSubmit: SubmitHandler<any>;
  initialValues: any;
  validate?: any;
  name: S;
  state: S | null;
  setState: Dispatch<SetStateAction<S | null>>;
  disabled: boolean;
};

/** Shared view/edit shell for price-list subresource cards. */
export function PricelistEditCard<S extends string>({
  header,
  view,
  viewHeader = header,
  edit,
  editHeader = header,
  onSubmit,
  initialValues,
  validate,
  state,
  setState,
  name,
  disabled,
}: PricelistEditCardProps<S>): ReactElement {
  if (state === name) {
    return (
      <EditContainer
        header={editHeader}
        cancel={() => setState(null)}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        testId={`edit-${name}-card`}
      >
        {edit}
      </EditContainer>
    );
  }

  const canEdit = !disabled && state == null;
  return (
    <ViewContainer header={viewHeader} edit={canEdit ? () => setState(name) : undefined} testId={`view-${name}-card`}>
      {view}
    </ViewContainer>
  );
}
